/**
 * S.T.A.R. portfolio entries — the shapes, and how to move between them.
 *
 * An expert's portfolio used to be one free-text blob per story, with
 * "Situation: … Task: …" existing only as placeholder text in a textarea. It is
 * now four fields, stored as four columns. Both shapes are live at once: every
 * entry written before the change is still a plain string, and nothing tries to
 * split one back apart — a guess at where Situation ends and Task begins would
 * be shown to the user as their own words.
 *
 * So: read both, write the structured one, and let a legacy entry stay what it
 * is until its author edits it.
 */

export interface StarPortfolioStory {
  title?: string | null;
  situation: string;
  task: string;
  action: string;
  result: string;
}

export type StarPartKey = "situation" | "task" | "action" | "result";

/** What the API can hand back for one entry. */
export type StarPortfolioEntry = StarPortfolioStory | string;

export const MAX_STAR_STORIES = 3;

/**
 * Length limits the backend enforces. Over-long text used to reach the server
 * and fail there — for the portfolio as an unhandled 500 (the story name goes
 * into a 255-character column) — so the forms check these first and say which
 * field is too long while the user is still typing.
 */
export const STAR_TITLE_MAX_LENGTH = 255;
/** Each part of an Expert portfolio story (StarPortfolioEntry). */
export const STAR_PORTFOLIO_PART_MAX_LENGTH = 4000;
/** Each section of a goal / program S.T.A.R. (StarUpdate). */
export const STAR_SECTION_MAX_LENGTH = 1000;

/** "Situation is too long …" — or undefined when it fits. */
export const tooLongMessage = (label: string, value: string | null | undefined, max: number) => {
  const length = value?.length ?? 0;
  return length > max
    ? `${label} is too long — ${length.toLocaleString()} of ${max.toLocaleString()} characters. Please shorten it.`
    : undefined;
};

/** Per-field length errors for one portfolio story; empty when everything fits. */
export const starStoryLengthErrors = (
  story: StarPortfolioStory,
): Partial<Record<"title" | StarPartKey, string>> => {
  const errors: Partial<Record<"title" | StarPartKey, string>> = {};
  const title = tooLongMessage("S.T.A.R. name", story.title, STAR_TITLE_MAX_LENGTH);
  if (title) errors.title = title;
  for (const { key, label } of STAR_PARTS) {
    const message = tooLongMessage(label, story[key], STAR_PORTFOLIO_PART_MAX_LENGTH);
    if (message) errors[key] = message;
  }
  return errors;
};

export const isStarStoryWithinLimits = (story: StarPortfolioStory): boolean =>
  Object.keys(starStoryLengthErrors(story)).length === 0;

/** The four parts, in the order they are asked for and shown. */
export const STAR_PARTS = [
  {
    key: "situation" as const,
    label: "Situation",
    hint: "The context — where you were and what was going on.",
    placeholder: "Our retention had been falling for three quarters and nobody owned the problem.",
  },
  {
    key: "task" as const,
    label: "Task",
    hint: "What you were responsible for.",
    placeholder: "I was asked to find out why, and to stop it inside two quarters.",
  },
  {
    key: "action" as const,
    label: "Action",
    hint: "What you actually did.",
    placeholder:
      "I interviewed 40 churned customers, rebuilt onboarding around the two steps most of them never finished…",
  },
  {
    key: "result" as const,
    label: "Result",
    hint: "What changed — with a number, where you have one.",
    placeholder: "Retention went from 71% to 86% in five months and held there.",
  },
];

export const emptyStarStory = (): StarPortfolioStory => ({
  title: "",
  situation: "",
  task: "",
  action: "",
  result: "",
});

export const isStructuredStar = (entry: unknown): entry is StarPortfolioStory =>
  typeof entry === "object" &&
  entry !== null &&
  !Array.isArray(entry) &&
  ["situation", "task", "action", "result"].some((k) => k in (entry as Record<string, unknown>));

const asString = (value: unknown): string => (typeof value === "string" ? value : "");

/** One entry from the API, in a shape the form and the display sites can use. */
export const toStarEntry = (raw: unknown): StarPortfolioEntry | null => {
  if (typeof raw === "string") return raw.trim() ? raw : null;
  if (!isStructuredStar(raw)) return null;
  const source = raw as unknown as Record<string, unknown>;
  return {
    title: asString(source.title),
    situation: asString(source.situation),
    task: asString(source.task),
    action: asString(source.action),
    result: asString(source.result),
  };
};

/** The whole portfolio, from whatever the API sent. Empty entries are dropped. */
export const toStarEntries = (value: unknown): StarPortfolioEntry[] => {
  const raw = Array.isArray(value) ? value : value ? [value] : [];
  return raw
    .map(toStarEntry)
    .filter((entry): entry is StarPortfolioEntry => entry !== null)
    .filter((entry) => typeof entry === "string" || !isStarStoryEmpty(entry));
};

/** Nothing typed in any part. Such an entry is never sent. */
export const isStarStoryEmpty = (story: StarPortfolioStory): boolean =>
  STAR_PARTS.every(({ key }) => !story[key]?.trim());

/** All four parts filled. Only a complete story becomes a `stars` row. */
export const isStarStoryComplete = (story: StarPortfolioStory): boolean =>
  STAR_PARTS.every(({ key }) => Boolean(story[key]?.trim()));

/**
 * The parts of a complete-enough story that are missing, for the field-level
 * error message. Returns [] for a story that is entirely blank, since a blank
 * optional story is not an error — the form drops it.
 */
export const missingStarParts = (story: StarPortfolioStory): string[] =>
  isStarStoryEmpty(story)
    ? []
    : STAR_PARTS.filter(({ key }) => !story[key]?.trim()).map((p) => p.label);

/** Trimmed, with the blank ones removed — the value to send. */
export const cleanStarStories = (stories: StarPortfolioStory[]): StarPortfolioStory[] =>
  stories
    .filter((story) => !isStarStoryEmpty(story))
    .map((story) => ({
      title: story.title?.trim() || null,
      situation: story.situation.trim(),
      task: story.task.trim(),
      action: story.action.trim(),
      result: story.result.trim(),
    }));

/**
 * A story as readable text, for the places that render one block of prose per
 * entry (the application recap, the review drawer) rather than laying the four
 * parts out themselves. A legacy string is already that, so it passes through.
 */
export const starEntryToText = (entry: StarPortfolioEntry): string => {
  if (typeof entry === "string") return entry;
  const body = STAR_PARTS.filter(({ key }) => entry[key]?.trim())
    .map(({ key, label }) => `**${label}:** ${entry[key].trim()}`)
    .join("\n");
  return entry.title?.trim() ? `**${entry.title.trim()}**\n${body}` : body;
};
